import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { 
  Shield, 
  Car, 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle, 
  AlertTriangle,
  User,
  CreditCard,
  Loader2,
  Mail,
  Send,
  Camera,
  X,
  XCircle,
  Gift,
  Info,
  HelpCircle,
  Upload,
  ImageIcon,
  Edit3,
  Search
} from "lucide-react";
import { toast } from "sonner";
import { 
  decodeVIN, 
  calculateQuote, 
  saveQuote, 
  createCheckoutSession,
  emailQuote,
  getQuote 
} from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, name: "Vehicle Details", icon: Car },
  { id: 2, name: "Driver History", icon: User },
  { id: 3, name: "Protection Level", icon: Shield },
  { id: 4, name: "Review & Pay", icon: CreditCard },
];

const COVERAGE_LEVELS = [
  { level: 1, name: "Ultimate Protection", percentage: 30, description: "Maximum value protection for your vehicle", badge: "Best Protection" },
  { level: 2, name: "Premium Protection", percentage: 25, description: "Enhanced coverage for significant value loss" },
  { level: 3, name: "Balanced Protection", percentage: 20, description: "Strong protection with balanced pricing", badge: "Most Popular", recommended: true },
  { level: 4, name: "Essential Protection", percentage: 15, description: "Basic coverage for potential resale value loss", badge: "Low Cost" },
];

const DEDUCTIBLE_OPTIONS = [
  { level: 1, name: "No Deductible", amount: 0 },
  { level: 2, name: "Standard", amount: 250 },
  { level: 3, name: "High", amount: 1000 },
  { level: 4, name: "Low Cost", amount: 2500 },
];

const PAYMENT_OPTIONS = [
  { value: "monthly", label: "Monthly", description: "12 payments" },
  { value: "quarterly", label: "Quarterly", description: "4 payments" },
  { value: "semi_annual", label: "Semi-Annual", description: "2 payments" },
  { value: "annual", label: "Annual", description: "1 payment (best value)" },
];

export default function QuotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailName, setEmailName] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Renewal state
  const [isRenewal, setIsRenewal] = useState(false);
  const [renewalToken, setRenewalToken] = useState(null);
  const [renewalData, setRenewalData] = useState(null);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(null);
  
  // Resume quote state
  const [isResuming, setIsResuming] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  
  // Camera scan state
  const [cameraOpen, setCameraOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [processingImage, setProcessingImage] = useState(false);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  
  // Form state
  const [vin, setVin] = useState("");
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [vehicleValue, setVehicleValue] = useState("");
  const [valueConfirmed, setValueConfirmed] = useState(false);
  
  // Market valuation state (MarketCheck Inventory Search)
  const [valuationStatus, setValuationStatus] = useState('idle'); // idle | loading | success | insufficient | error
  const [valuationResult, setValuationResult] = useState(null); // {estimate, range_lower, range_upper, count, scope}
  const [valuationError, setValuationError] = useState(null);
  const [valuationAccepted, setValuationAccepted] = useState(false);
  const [manualAdjust, setManualAdjust] = useState(false);
  const [adjustedValue, setAdjustedValue] = useState("");
  const [valuationInputHash, setValuationInputHash] = useState("");
  
  // Eligibility error state for consumer-friendly messaging
  const [eligibilityError, setEligibilityError] = useState(null);
  const eligibilityAlertRef = useRef(null);
  
  // Vehicle value eligibility state
  const [valueEligibilityError, setValueEligibilityError] = useState(null);
  const valueInputRef = useRef(null);
  const MINIMUM_VEHICLE_VALUE = 25000;
  
  const [driverRecord, setDriverRecord] = useState({
    has_dui: false,
    has_license_suspension: false,
    has_reckless_driving: false,
    speeding_tickets: 0,
    cited_accidents: 0,
    uncited_accidents: 0,
    is_first_responder_military: false,
  });
  
  const [coverageLevel, setCoverageLevel] = useState(3);
  const [deductibleLevel, setDeductibleLevel] = useState(1);
  const [paymentFrequency, setPaymentFrequency] = useState("annual");
  
  // Payment consent state (must be unchecked by default)
  const [saveCardConsent, setSaveCardConsent] = useState(false);
  
  const [quote, setQuote] = useState(null);

  const progress = (currentStep / STEPS.length) * 100;

  // Check for renewal token on mount
  useEffect(() => {
    const renewalTokenParam = searchParams.get('renewal');
    if (renewalTokenParam) {
      setRenewalToken(renewalTokenParam);
      fetchRenewalData(renewalTokenParam);
    }
  }, [searchParams]);

  // Check for resume quote ID on mount
  useEffect(() => {
    const resumeQuoteId = searchParams.get('resume');
    if (resumeQuoteId) {
      fetchResumeQuote(resumeQuoteId);
    }
  }, [searchParams]);

  // Fetch and restore a saved quote for resume flow
  const fetchResumeQuote = async (quoteId) => {
    setResumeLoading(true);
    try {
      const savedQuote = await getQuote(quoteId);
      
      if (savedQuote) {
        setIsResuming(true);
        
        // Restore VIN and vehicle info
        if (savedQuote.vin) {
          setVin(savedQuote.vin);
        }
        if (savedQuote.vehicle_info) {
          setVehicleInfo(savedQuote.vehicle_info);
          setVehicleValue(savedQuote.vehicle_info.estimated_value?.toString() || "");
        }
        
        // Restore driver record
        if (savedQuote.driver_record) {
          setDriverRecord(savedQuote.driver_record);
        }
        
        // Restore coverage options
        if (savedQuote.coverage_level) {
          setCoverageLevel(savedQuote.coverage_level);
        }
        if (savedQuote.deductible_level) {
          setDeductibleLevel(savedQuote.deductible_level);
        }
        if (savedQuote.payment_frequency) {
          setPaymentFrequency(savedQuote.payment_frequency);
        }
        
        // Set the full quote object
        setQuote(savedQuote);
        
        // Navigate to Review & Pay step (step 4)
        setCurrentStep(4);
        
        toast.success("Your quote has been loaded. Review your details and complete your purchase.");
      }
    } catch (err) {
      console.error('Failed to load saved quote:', err);
      toast.error("Unable to load your saved quote. Please try generating a new one.");
    } finally {
      setResumeLoading(false);
    }
  };

  const fetchRenewalData = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/renewal/intent/${token}`);
      const data = await response.json();
      
      if (data.valid && data.quote_data) {
        setIsRenewal(true);
        setRenewalData(data);
        
        // Pre-fill VIN and vehicle info
        const quoteData = data.quote_data;
        if (quoteData.vin) {
          setVin(quoteData.vin);
        }
        if (quoteData.vehicle_info) {
          setVehicleInfo(quoteData.vehicle_info);
          setVehicleValue(quoteData.vehicle_info.estimated_value?.toString() || "");
        }
        if (quoteData.coverage_level) {
          setCoverageLevel(quoteData.coverage_level);
        }
        if (quoteData.deductible_level) {
          setDeductibleLevel(quoteData.deductible_level);
        }
        
        // Set loyalty discount info
        if (quoteData.loyalty_discount) {
          setLoyaltyDiscount(quoteData.loyalty_discount);
        }
        
        toast.success("Your policy information has been loaded. Review and update as needed.");
      }
    } catch (err) {
      console.error('Failed to load renewal data:', err);
    }
  };

  // Camera functions
  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Camera not supported on this device or browser.");
      return;
    }

    // Open dialog first so the video element mounts
    setCameraOpen(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      // Attach stream — videoRef should be available now since dialog is open
      attachStream(stream);
    } catch (error) {
      console.error('Camera access error:', error);
      setCameraOpen(false);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error("Camera permission denied. Please allow camera access in your browser settings and try again.");
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error("No camera found on this device.");
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error("Camera is in use by another application. Please close other apps using the camera.");
      } else if (error.name === 'OverconstrainedError') {
        setCameraOpen(true);
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          streamRef.current = stream;
          attachStream(stream);
          return;
        } catch (retryError) {
          setCameraOpen(false);
          toast.error("Unable to access camera. Please check your device settings.");
        }
      } else {
        toast.error("Unable to access camera. Please check permissions and try again.");
      }
    }
  };

  const attachStream = (stream) => {
    // Try immediately, then retry with a short delay for Portal mount timing
    const tryAttach = () => {
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
        return true;
      }
      return false;
    };
    
    if (!tryAttach()) {
      // Retry after Portal renders
      setTimeout(() => {
        if (!tryAttach()) {
          setTimeout(tryAttach, 200);
        }
      }, 50);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
    setScanning(false);
    setScanProgress(0);
  };

  const captureAndScan = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setScanning(true);
    setScanProgress(30);

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Compress the frame for fast AI processing
    const compressedImage = await compressImage(canvas.toDataURL('image/jpeg', 0.7));
    setScanProgress(60);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vin/extract-from-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_base64: compressedImage }),
      });

      const data = await response.json();
      setScanProgress(100);

      if (data.success && data.vin) {
        setVin(data.vin);
        toast.success(`VIN detected: ${data.vin}`);
        stopCamera();
      } else {
        toast.error(data.message || "No VIN detected. Please reposition and try again.");
      }
    } catch (error) {
      console.error('Camera scan error:', error);
      toast.error("Failed to scan VIN. Please try again or upload a photo.");
    } finally {
      setScanning(false);
      setScanProgress(0);
    }
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Build fingerprint to detect stale valuation
  const buildValuationHash = (info) => {
    if (!info) return "";
    return `${info.vin || ""}|${info.year}|${info.make}|${info.model}|${info.trim || ""}`;
  };

  // Fetch market valuation from MarketCheck via backend proxy
  const fetchMarketEstimate = async () => {
    if (!vehicleInfo) return;

    const hash = buildValuationHash(vehicleInfo);
    if (hash === valuationInputHash && valuationResult) return;

    setValuationStatus('loading');
    setValuationError(null);
    setValuationResult(null);
    setValuationAccepted(false);
    setManualAdjust(false);

    try {
      const body = {};
      if (vehicleInfo.vin && vehicleInfo.vin.length === 17) body.vin = vehicleInfo.vin;
      if (vehicleInfo.year) body.year = parseInt(vehicleInfo.year);
      if (vehicleInfo.make) body.make = vehicleInfo.make;
      if (vehicleInfo.model) body.model = vehicleInfo.model;
      if (vehicleInfo.trim) body.trim = vehicleInfo.trim;

      const resp = await fetch(`${API_URL}/api/vehicle-market-value`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Unable to retrieve market estimate');
      }

      const data = await resp.json();
      setValuationInputHash(hash);

      if (data.status === 'success') {
        setValuationResult(data);
        setValuationStatus('success');
      } else if (data.status === 'insufficient_data') {
        setValuationError(data.message || 'Not enough comparable listings found.');
        setValuationStatus('insufficient');
      } else {
        setValuationError('Unable to retrieve market estimate.');
        setValuationStatus('error');
      }
    } catch (err) {
      setValuationError(err.message);
      setValuationStatus('error');
    }
  };

  // Auto-fetch market valuation when vehicle info changes
  useEffect(() => {
    if (vehicleInfo && !vehicleInfo.error && vehicleInfo.year && vehicleInfo.make && vehicleInfo.model) {
      const newHash = buildValuationHash(vehicleInfo);
      if (newHash !== valuationInputHash) {
        // Reset and auto-fetch
        setValuationStatus('idle');
        setValuationResult(null);
        setValuationAccepted(false);
        setValuationInputHash("");
        setValueEligibilityError(null);
        // Trigger fetch automatically
        fetchMarketEstimate();
      }
    }
  }, [vehicleInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Accept market estimate
  const handleAcceptValuation = () => {
    if (!valuationResult) return;
    const estimate = valuationResult.estimate;
    setVehicleValue(estimate.toString());
    setValuationAccepted(true);
    setManualAdjust(false);
    setValueConfirmed(true);
    // Check $25K minimum
    if (estimate < MINIMUM_VEHICLE_VALUE) {
      setValueEligibilityError({
        headline: "This vehicle does not meet our eligibility requirements.",
        body: `The estimated market value of this vehicle is ${formatCurrency(estimate)}, which is below the $25,000 minimum required for LossPay Diminished Value Protection.`,
        helperText: "If you believe this value does not reflect your vehicle's actual condition or market worth, you may adjust it above."
      });
    } else {
      setValueEligibilityError(null);
    }
  };

  // Start manual adjustment
  const handleStartAdjust = () => {
    setManualAdjust(true);
    setAdjustedValue(valuationResult ? valuationResult.estimate.toString() : vehicleValue);
  };

  // Save manual adjustment
  const handleSaveAdjust = () => {
    const numVal = parseInt(adjustedValue.replace(/[^0-9]/g, ''), 10);
    if (!numVal || numVal <= 0) return;
    setVehicleValue(numVal.toString());
    setManualAdjust(false);
    setValuationAccepted(true);
    setValueConfirmed(true);
    if (numVal < MINIMUM_VEHICLE_VALUE) {
      setValueEligibilityError({
        headline: "We're unable to provide coverage for this vehicle.",
        body: "LossPay is designed for vehicles valued at $25,000 or more.",
        helperText: "You can adjust the value to reflect your vehicle's current market condition."
      });
    } else {
      setValueEligibilityError(null);
    }
  };

  // Cancel manual adjustment
  const handleCancelAdjust = () => {
    setManualAdjust(false);
    setAdjustedValue("");
  };

  // Image upload functions
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file (JPG, PNG, etc.)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('Image too large. Please select an image under 10MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
        setImageUploadOpen(true);
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  // Compress image for fast VIN extraction
  const compressImage = (dataUrl, maxWidth = 512, quality = 0.5) => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Image compression timed out'));
      }, 10000);
      
      const img = new Image();
      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Failed to load image for compression'));
      };
      img.src = dataUrl;
    });
  };

  const processUploadedImage = async () => {
    if (!uploadedImage) return;
    
    setProcessingImage(true);
    setScanProgress(10);
    
    try {
      // Compress image for fast processing
      setScanProgress(20);
      const compressedImage = await compressImage(uploadedImage);
      
      // Use AI-powered VIN extraction via backend
      setScanProgress(40);
      
      const response = await fetch(`${API_URL}/api/vin/extract-from-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_base64: compressedImage
        })
      });
      
      setScanProgress(85);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to process image');
      }
      
      const result = await response.json();
      setScanProgress(100);
      
      if (result.success && result.vin) {
        setVin(result.vin);
        toast.success(`VIN detected: ${result.vin}`);
        setImageUploadOpen(false);
        setUploadedImage(null);
      } else {
        toast.error(result.message || "No VIN detected. Please try a clearer photo or enter the VIN manually.");
      }
    } catch (error) {
      console.error('Image processing error:', error);
      toast.error(error.message || "Failed to process image. Please try again or enter the VIN manually.");
    } finally {
      setProcessingImage(false);
      setScanProgress(0);
    }
  };

  const cancelImageUpload = () => {
    setImageUploadOpen(false);
    setUploadedImage(null);
    setProcessingImage(false);
    setScanProgress(0);
  };

  const handleDecodeVIN = async () => {
    if (!vin || vin.length < 10) {
      toast.error("Please enter a valid VIN (at least 10 characters)");
      return;
    }

    setLoading(true);
    setEligibilityError(null); // Clear any previous eligibility errors
    
    try {
      const data = await decodeVIN(vin);
      
      // Handle rate limiting
      if (data.rate_limited) {
        // If we have cached data, still use it
        if (data.make) {
          setVehicleInfo(data);
          setVehicleValue(data.estimated_value?.toString() || "35000");
          toast.info(data.message || "Using cached vehicle data. You can continue with your quote.");
        } else {
          toast.warning(data.message || "Please try again shortly.");
        }
        return;
      }
      
      // Handle eligibility rejection with consumer-friendly messaging
      if (data.eligible === false && data.user_message) {
        setEligibilityError({
          headline: data.user_message.headline,
          body: data.user_message.body,
          helperText: data.user_message.helper_text,
          reasonCode: data.reason_code,
          vehicleInfo: data.vehicle_info
        });
        setVehicleInfo(null);
        setVehicleValue("");
        
        // Scroll to the eligibility alert
        setTimeout(() => {
          eligibilityAlertRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }, 100);
        
        return;
      }
      
      // Handle legacy error format (shouldn't happen but just in case)
      if (data.error && !data.make) {
        setEligibilityError({
          headline: "We're unable to verify this VIN.",
          body: "Please review the VIN and try again. If the issue continues, contact support for assistance.",
          helperText: "Tip: The VIN is typically found on your vehicle registration, insurance card, or on the driver's side dashboard near the windshield.",
          reasonCode: "unknown_error"
        });
        setVehicleInfo(null);
        return;
      }
      
      // Success - vehicle is eligible
      setVehicleInfo(data);
      setVehicleValue(data.estimated_value?.toString() || "35000");
      toast.success(`Vehicle found: ${data.year} ${data.make} ${data.model}${data.trim ? ` ${data.trim}` : ''}`);
      
    } catch (error) {
      // Handle API errors gracefully
      const errorMessage = error.response?.data?.detail || error.message;
      
      // Check if this is a structured error from the backend
      if (error.response?.data?.user_message) {
        const errData = error.response.data;
        setEligibilityError({
          headline: errData.user_message.headline,
          body: errData.user_message.body,
          helperText: errData.user_message.helper_text,
          reasonCode: errData.reason_code
        });
      } else {
        // Fallback for unexpected errors
        setEligibilityError({
          headline: "We're unable to verify this VIN.",
          body: "Please review the VIN and try again. If the issue continues, contact support for assistance.",
          helperText: "Tip: Make sure you've entered all 17 characters correctly.",
          reasonCode: "api_error"
        });
      }
      
      setVehicleInfo(null);
      
      // Scroll to the eligibility alert
      setTimeout(() => {
        eligibilityAlertRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
      
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateQuote = useCallback(async () => {
    setLoading(true);
    try {
      // Get reCAPTCHA token if available
      let recaptchaToken = null;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('quote_calculation');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA execution failed:', recaptchaError);
          // Continue without token if reCAPTCHA fails
        }
      }

      const quoteData = {
        vin: vin.toUpperCase(),
        vehicle_value: parseFloat(vehicleValue) || 35000,
        coverage_level: coverageLevel,
        deductible_level: deductibleLevel,
        driver_record: driverRecord,
        payment_frequency: paymentFrequency,
        recaptcha_token: recaptchaToken,
      };
      
      const result = await calculateQuote(quoteData);
      
      // Handle rate limiting
      if (result.rate_limited) {
        // If we have cached quote data, use it
        if (result.quote_number) {
          setQuote(result);
          toast.info(result.message || "Using your existing quote.");
        } else {
          toast.warning(result.message || "Please try again shortly.");
        }
        return;
      }
      
      setQuote(result);
      
      if (!result.is_eligible) {
        toast.error(result.rejection_reason || "Unfortunately, we cannot offer coverage at this time.");
      }
    } catch (error) {
      if (error.message?.includes('suspicious activity')) {
        toast.error("Request blocked. Please try again later.");
      } else {
        toast.error("Failed to calculate quote. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [executeRecaptcha, vin, vehicleValue, coverageLevel, deductibleLevel, driverRecord, paymentFrequency]);

  const handleProceedToPayment = async () => {
    if (!quote || !quote.is_eligible) {
      toast.error("Please complete a valid quote first");
      return;
    }

    setLoading(true);
    try {
      // Save the quote first
      await saveQuote(quote);
      
      // Create checkout session with consent preference
      const session = await createCheckoutSession(quote.id, saveCardConsent);
      
      // Redirect to Stripe
      if (session.checkout_url) {
        window.location.href = session.checkout_url;
      }
    } catch (error) {
      toast.error("Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailQuote = async () => {
    if (!emailAddress) {
      toast.error("Please enter an email address");
      return;
    }

    if (!quote || !quote.is_eligible) {
      toast.error("Please calculate a valid quote first");
      return;
    }

    setSendingEmail(true);
    try {
      // Save quote first
      await saveQuote(quote);
      
      // Send email
      await emailQuote(quote.id, emailAddress, emailName);
      toast.success(`Quote sent to ${emailAddress}`);
      setEmailDialogOpen(false);
      setEmailAddress("");
      setEmailName("");
    } catch (error) {
      toast.error("Failed to send email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  // Check if user has any major violations that would disqualify them
  const hasMajorViolations = driverRecord.has_dui || driverRecord.has_license_suspension || driverRecord.has_reckless_driving;

  const nextStep = () => {
    if (currentStep === 1 && !vehicleInfo) {
      toast.error("Please decode your VIN first");
      return;
    }
    
    // Validate minimum vehicle value on Step 1
    if (currentStep === 1) {
      const numValue = parseInt(vehicleValue, 10) || 0;
      if (numValue < MINIMUM_VEHICLE_VALUE) {
        setValueEligibilityError({
          headline: "We're unable to provide coverage for this vehicle.",
          body: "LossPay is designed for vehicles valued at $25,000 or more. Please check the value entered or adjust if needed based on your vehicle's condition.",
          helperText: "You can adjust the value to reflect your vehicle's current market condition."
        });
        // Scroll to the value input
        setTimeout(() => {
          valueInputRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          valueInputRef.current?.focus();
        }, 100);
        return;
      }
    }

    // Block progression from Step 2 if user has major violations
    if (currentStep === 2 && hasMajorViolations) {
      // Don't proceed - the UI will show the disqualification notice
      return;
    }
    
    if (currentStep < STEPS.length) {
      if (currentStep === 3) {
        handleCalculateQuote();
      }
      setCurrentStep(currentStep + 1);
      // Scroll to top of page - use 'auto' on mobile to prevent glitches
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: window.innerWidth < 640 ? 'auto' : 'smooth' });
      }, 50);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top of page - use 'auto' on mobile to prevent glitches
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: window.innerWidth < 640 ? 'auto' : 'smooth' });
      }, 50);
    }
  };

  const formatCurrency = (amount) => {
    const num = Number(amount);
    const hasDecimals = num % 1 !== 0;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(num);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Resume Loading Overlay */}
      {resumeLoading && (
        <div className="fixed inset-0 bg-white/90 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Loading Your Quote</h3>
            <p className="text-slate-600">Please wait while we retrieve your saved quote...</p>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a 
            href="https://losspay.com"
            className="flex items-center transition-opacity hover:opacity-80"
          >
            <img 
              src="/losspay-header-logo.png" 
              alt="LossPay" 
              className="h-[28px] sm:h-[36px] w-auto"
              data-testid="losspay-logo"
            />
          </a>
          <span className="text-xs sm:text-sm text-slate-700 hidden sm:block">Diminished Value Quote</span>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center mb-4">
            {STEPS.map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center gap-2 ${
                  currentStep >= step.id ? 'text-blue-600' : 'text-slate-600'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep >= step.id ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700'
                }`}>
                  {currentStep > step.id ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <step.icon className="h-4 w-4" />
                  )}
                </div>
                <span className="hidden sm:inline text-sm font-medium">{step.name}</span>
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8 quote-form-container">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          {/* Step 1: Vehicle Details */}
          {currentStep === 1 && (
            <div className="p-6 sm:p-8 animate-fade-in">
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Vehicle Details
              </h2>
              <p className="text-slate-700 mb-6">Enter your VIN to get started</p>

              <div className="space-y-6">
                <div>
                  <Label htmlFor="vin" className="text-sm font-medium text-slate-700">
                    Vehicle Identification Number (VIN)
                  </Label>
                  <div className="flex gap-2 mt-2">
                    <div className="relative flex-1">
                      <Input
                        id="vin"
                        data-testid="vin-input"
                        placeholder="Enter 17-character VIN"
                        value={vin}
                        onChange={(e) => {
                          setVin(e.target.value.toUpperCase());
                          // Clear eligibility error when user modifies VIN
                          if (eligibilityError) {
                            setEligibilityError(null);
                          }
                        }}
                        maxLength={17}
                        className="font-mono tracking-wider pr-12"
                      />
                      {/* Camera icon inside input */}
                      <button
                        type="button"
                        data-testid="scan-vin-btn"
                        onClick={() => setScanModalOpen(true)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-[#3B5BDB]/10 hover:bg-[#3B5BDB]/20 transition-colors"
                        title="Scan or upload VIN photo"
                      >
                        <Camera className="h-5 w-5 text-[#3B5BDB]" />
                      </button>
                    </div>
                    {/* Hidden file input for image upload */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button 
                      data-testid="decode-vin-btn"
                      onClick={handleDecodeVIN}
                      disabled={loading || vin.length < 10}
                      variant="blue"
                      className="whitespace-nowrap"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Decode"}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-700 mt-2">
                    Find your VIN on your registration, insurance card, or driver-side door jamb
                  </p>
                </div>

                {/* Consumer-Friendly Eligibility Alert */}
                {eligibilityError && (
                  <div 
                    ref={eligibilityAlertRef}
                    data-testid="eligibility-alert"
                    className="animate-fade-in rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-6 shadow-sm"
                  >
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <Info className="h-5 w-5 text-amber-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {eligibilityError.headline}
                        </h3>
                        <p className="text-sm text-slate-700 leading-relaxed mb-4">
                          {eligibilityError.body}
                        </p>
                        
                        {/* Show detected vehicle info if available */}
                        {eligibilityError.vehicleInfo && eligibilityError.vehicleInfo.make && (
                          <div className="bg-white/60 rounded-lg p-3 mb-4 border border-amber-100">
                            <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">
                              Detected Vehicle
                            </p>
                            <p className="text-sm font-medium text-slate-800">
                              {[
                                eligibilityError.vehicleInfo.year,
                                eligibilityError.vehicleInfo.make,
                                eligibilityError.vehicleInfo.model
                              ].filter(Boolean).join(' ') || 'Unknown'}
                              {eligibilityError.vehicleInfo.vehicle_type && (
                                <span className="text-slate-600 font-normal">
                                  {' '}• {eligibilityError.vehicleInfo.vehicle_type}
                                </span>
                              )}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex items-start gap-2 text-sm text-slate-600">
                          <HelpCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
                          <p>{eligibilityError.helperText}</p>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="mt-4 flex flex-wrap gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEligibilityError(null);
                              setVin("");
                            }}
                            className="border-amber-300 text-amber-700 hover:bg-amber-100"
                          >
                            Clear & Try Again
                          </Button>
                          <a 
                            href="/support" 
                            className="inline-flex items-center justify-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Contact Support
                            <ArrowRight className="ml-1 h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {vehicleInfo && (
                  <div className="bg-slate-50 rounded-xl p-4 sm:p-6 border border-slate-200 animate-fade-in overflow-hidden">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                      Vehicle Found
                    </h3>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div className="min-w-0">
                        <p className="text-xs text-slate-700">Year</p>
                        <p className="font-semibold text-slate-900">{vehicleInfo.year || "N/A"}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-700">Make</p>
                        <p className="font-semibold text-slate-900 truncate">{vehicleInfo.make || "N/A"}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-700">Model</p>
                        <p className="font-semibold text-slate-900 truncate">{vehicleInfo.model || "N/A"}</p>
                      </div>
                      {vehicleInfo.trim && (
                        <div className="min-w-0">
                          <p className="text-xs text-slate-700">Trim</p>
                          <p className="font-semibold text-slate-900 truncate">{vehicleInfo.trim}</p>
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs text-slate-700">Body Class</p>
                        <p className="font-semibold text-slate-900 text-sm break-words leading-tight">{vehicleInfo.body_class || "N/A"}</p>
                      </div>
                    </div>

                    {/* ── Market Valuation Section ── */}
                    <div className="mt-4 pt-4 border-t border-slate-200">

                      {/* Ready state — "Get Market Estimate" button + manual entry */}
                      {valuationStatus === 'idle' && !valuationAccepted && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-sm font-medium text-slate-700">Estimated Vehicle Value</Label>
                            <button
                              type="button"
                              onClick={fetchMarketEstimate}
                              disabled={!vehicleInfo || !vehicleInfo.year || !vehicleInfo.make || !vehicleInfo.model}
                              className="text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center gap-1.5"
                              data-testid="get-market-estimate-btn"
                            >
                              <Search className="h-3.5 w-3.5" />
                              Get market estimate
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-700 font-medium">$</span>
                            <Input
                              id="value"
                              ref={valueInputRef}
                              data-testid="vehicle-value-input"
                              type="text"
                              inputMode="numeric"
                              value={vehicleValue ? Number(vehicleValue).toLocaleString() : ""}
                              onChange={(e) => {
                                const rawValue = e.target.value.replace(/[^0-9]/g, '');
                                setVehicleValue(rawValue);
                                setValueConfirmed(false);
                                if (rawValue && parseInt(rawValue, 10) >= MINIMUM_VEHICLE_VALUE) {
                                  setValueEligibilityError(null);
                                }
                              }}
                              onBlur={() => {
                                const numValue = parseInt(vehicleValue, 10) || 0;
                                if (vehicleValue && numValue < MINIMUM_VEHICLE_VALUE) {
                                  setValueEligibilityError({
                                    headline: "We're unable to provide coverage for this vehicle.",
                                    body: "LossPay is designed for vehicles valued at $25,000 or more.",
                                    helperText: "You can adjust the value to reflect your vehicle's current market condition."
                                  });
                                }
                              }}
                              className={`font-mono ${valueEligibilityError ? 'border-amber-400' : ''}`}
                              placeholder="35,000"
                            />
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Minimum vehicle value: $25,000</p>
                        </div>
                      )}

                      {/* Loading state */}
                      {valuationStatus === 'loading' && (
                        <div className="p-5 bg-slate-50 rounded-xl border border-slate-200" role="status" aria-live="polite">
                          <div className="flex items-center gap-3">
                            <Loader2 className="h-5 w-5 animate-spin text-blue-600 flex-shrink-0" />
                            <p className="text-sm font-medium text-slate-700">Searching comparable active listings...</p>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">This usually takes a few seconds</p>
                        </div>
                      )}

                      {/* Error / Insufficient data — show manual entry */}
                      {(valuationStatus === 'error' || valuationStatus === 'insufficient') && !valuationAccepted && (
                        <div>
                          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 mb-4" role="alert">
                            <div className="flex items-start gap-3">
                              <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm text-amber-800">{valuationError || 'Unable to retrieve market data.'}</p>
                                <button
                                  type="button"
                                  onClick={fetchMarketEstimate}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-700 mt-2 underline"
                                  data-testid="valuation-retry-btn"
                                >
                                  Try again
                                </button>
                              </div>
                            </div>
                          </div>
                          <Label htmlFor="value" className="text-sm font-medium text-slate-700">Enter Vehicle Value Manually</Label>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-slate-700 font-medium">$</span>
                            <Input id="value" ref={valueInputRef} data-testid="vehicle-value-input" type="text" inputMode="numeric"
                              value={vehicleValue ? Number(vehicleValue).toLocaleString() : ""}
                              onChange={(e) => { const r = e.target.value.replace(/[^0-9]/g, ''); setVehicleValue(r); setValueConfirmed(false); if (r && parseInt(r,10) >= MINIMUM_VEHICLE_VALUE) setValueEligibilityError(null); }}
                              onBlur={() => { const n = parseInt(vehicleValue,10)||0; if (vehicleValue && n < MINIMUM_VEHICLE_VALUE) setValueEligibilityError({headline:"We're unable to provide coverage for this vehicle.",body:"LossPay is designed for vehicles valued at $25,000 or more.",helperText:"You can adjust the value to reflect your vehicle's current market condition."}); }}
                              className={`font-mono ${valueEligibilityError ? 'border-amber-400' : ''}`} placeholder="35,000" />
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Minimum vehicle value: $25,000</p>
                        </div>
                      )}

                      {/* Success — show valuation card */}
                      {valuationStatus === 'success' && valuationResult && !valuationAccepted && !manualAdjust && (
                        <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-slate-50 overflow-hidden" data-testid="valuation-card">
                          <div className="p-5">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Estimated Market Value</p>
                            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                              {formatCurrency(valuationResult.estimate)}
                            </p>
                            {valuationResult.range_lower && valuationResult.range_upper && (
                              <p className="text-sm text-slate-600 mt-1">
                                Comparable asking price range: {formatCurrency(valuationResult.range_lower)} – {formatCurrency(valuationResult.range_upper)}
                              </p>
                            )}
                            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                              This estimate reflects advertised prices for comparable vehicles currently listed for sale. It is not an appraisal, purchase offer, guaranteed sale price, or insurance settlement value.
                            </p>
                          </div>
                          <div className="flex border-t border-blue-200">
                            <button type="button" onClick={handleAcceptValuation}
                              className="flex-1 py-3 text-sm font-semibold text-blue-700 hover:bg-blue-100 transition-colors border-r border-blue-200"
                              data-testid="accept-valuation-btn">
                              <CheckCircle className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                              This looks right
                            </button>
                            <button type="button" onClick={handleStartAdjust}
                              className="flex-1 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                              data-testid="adjust-valuation-btn">
                              <Edit3 className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                              Adjust value
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Manual adjustment mode */}
                      {manualAdjust && (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="manual-adjust-card">
                          <p className="text-sm font-medium text-slate-700 mb-3">Enter your estimated vehicle value</p>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-700 font-medium">$</span>
                            <Input data-testid="manual-value-input" type="text" inputMode="numeric" autoFocus
                              value={adjustedValue ? Number(adjustedValue.replace(/[^0-9]/g, '')).toLocaleString() : ""}
                              onChange={(e) => setAdjustedValue(e.target.value.replace(/[^0-9]/g, ''))}
                              className="font-mono" placeholder="35,000" />
                          </div>
                          {valuationResult && (
                            <p className="text-xs text-slate-500 mt-2">Market estimate: {formatCurrency(valuationResult.estimate)}</p>
                          )}
                          <p className="text-xs text-slate-500 mt-1">Minimum vehicle value: $25,000</p>
                          <div className="flex gap-2 mt-3">
                            <Button type="button" size="sm" variant="blue" onClick={handleSaveAdjust} data-testid="save-adjust-btn">Save</Button>
                            <Button type="button" size="sm" variant="outline" onClick={handleCancelAdjust}>Cancel</Button>
                          </div>
                        </div>
                      )}

                      {/* Accepted / Confirmed state */}
                      {valuationAccepted && !manualAdjust && (
                        <div className={`rounded-xl border p-5 ${
                          valueEligibilityError 
                            ? 'border-amber-200 bg-amber-50' 
                            : 'border-green-200 bg-green-50'
                        }`} data-testid="value-confirmed-card">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
                                valueEligibilityError ? 'text-amber-700' : 'text-green-700'
                              }`}>
                                {valueEligibilityError 
                                  ? <><Info className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />Estimated Vehicle Value</>
                                  : <><CheckCircle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />Confirmed Vehicle Value</>
                                }
                              </p>
                              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {formatCurrency(parseInt(vehicleValue, 10))}
                              </p>
                              {valuationResult && parseInt(vehicleValue, 10) !== valuationResult.estimate && (
                                <p className="text-xs text-slate-500 mt-1">Market estimate was {formatCurrency(valuationResult.estimate)}</p>
                              )}
                            </div>
                            <button type="button" onClick={handleStartAdjust}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium underline flex-shrink-0"
                              data-testid="edit-value-btn">Edit</button>
                          </div>
                        </div>
                      )}

                      {/* Value Eligibility Error Message */}
                      {valueEligibilityError && (
                        <div 
                          className="mt-3 p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl animate-fade-in"
                          data-testid="value-eligibility-error"
                        >
                          <div className="flex gap-3">
                            <div className="flex-shrink-0">
                              <Info className="h-5 w-5 text-amber-600 mt-0.5" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {valueEligibilityError.headline}
                              </p>
                              <p className="text-sm text-slate-700 mt-1">
                                {valueEligibilityError.body}
                              </p>
                              {valueEligibilityError.helperText && (
                                <p className="text-xs text-slate-600 mt-2 flex items-center gap-1">
                                  <HelpCircle className="h-3 w-3" />
                                  {valueEligibilityError.helperText}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Vehicle Value Acknowledgement */}
                      {vehicleValue && parseInt(vehicleValue, 10) >= MINIMUM_VEHICLE_VALUE && !valueEligibilityError && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <div className="flex items-start space-x-3">
                            <Checkbox
                              id="value_confirmed"
                              data-testid="value-confirmed-checkbox"
                              checked={valueConfirmed}
                              onCheckedChange={(checked) => setValueConfirmed(checked)}
                            />
                            <Label htmlFor="value_confirmed" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                              Please confirm the current market value of your vehicle. Your selected coverage limits and potential claim payout calculations are based on this amount.
                            </Label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Driver History */}
          {currentStep === 2 && (
            <div className="p-6 sm:p-8 animate-fade-in">
              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Driver History
                  </h2>
                </div>
                <p className="text-slate-600 ml-12">Tell us about your driving record from the past 3 years</p>
              </div>

              <div className="space-y-8">
                {/* Section 1: Major Violations */}
                <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="px-5 py-4 bg-slate-100/50 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 text-sm font-bold">1</span>
                      Major Violations
                    </h3>
                  </div>
                  <div className="p-5 space-y-4">
                    {/* DUI */}
                    <div 
                      className={`flex items-center justify-between gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        driverRecord.has_dui 
                          ? 'border-amber-400 bg-amber-50' 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      onClick={() => setDriverRecord({ ...driverRecord, has_dui: !driverRecord.has_dui })}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className={`p-2.5 rounded-lg flex-shrink-0 ${driverRecord.has_dui ? 'bg-amber-100' : 'bg-slate-100'}`}>
                          <AlertTriangle className={`h-5 w-5 ${driverRecord.has_dui ? 'text-amber-600' : 'text-slate-400'}`} />
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-slate-800">DUI Conviction</span>
                          <p className="text-sm text-slate-500">In the past 3 years</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="dui-checkbox"
                        role="switch"
                        aria-checked={driverRecord.has_dui}
                        className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                          driverRecord.has_dui ? 'bg-amber-500' : 'bg-slate-300'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDriverRecord({ ...driverRecord, has_dui: !driverRecord.has_dui });
                        }}
                      >
                        <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                          driverRecord.has_dui ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>

                    {/* License Suspension */}
                    <div 
                      className={`flex items-center justify-between gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        driverRecord.has_license_suspension 
                          ? 'border-amber-400 bg-amber-50' 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      onClick={() => setDriverRecord({ ...driverRecord, has_license_suspension: !driverRecord.has_license_suspension })}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className={`p-2.5 rounded-lg flex-shrink-0 ${driverRecord.has_license_suspension ? 'bg-amber-100' : 'bg-slate-100'}`}>
                          <AlertTriangle className={`h-5 w-5 ${driverRecord.has_license_suspension ? 'text-amber-600' : 'text-slate-400'}`} />
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-slate-800">License Suspension</span>
                          <p className="text-sm text-slate-500">In the past 3 years</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="suspension-checkbox"
                        role="switch"
                        aria-checked={driverRecord.has_license_suspension}
                        className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                          driverRecord.has_license_suspension ? 'bg-amber-500' : 'bg-slate-300'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDriverRecord({ ...driverRecord, has_license_suspension: !driverRecord.has_license_suspension });
                        }}
                      >
                        <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                          driverRecord.has_license_suspension ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>

                    {/* Reckless Driving */}
                    <div 
                      className={`flex items-center justify-between gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        driverRecord.has_reckless_driving 
                          ? 'border-amber-400 bg-amber-50' 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      onClick={() => setDriverRecord({ ...driverRecord, has_reckless_driving: !driverRecord.has_reckless_driving })}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className={`p-2.5 rounded-lg flex-shrink-0 ${driverRecord.has_reckless_driving ? 'bg-amber-100' : 'bg-slate-100'}`}>
                          <AlertTriangle className={`h-5 w-5 ${driverRecord.has_reckless_driving ? 'text-amber-600' : 'text-slate-400'}`} />
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-slate-800">Reckless Driving Citation</span>
                          <p className="text-sm text-slate-500">In the past 3 years</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="reckless-checkbox"
                        role="switch"
                        aria-checked={driverRecord.has_reckless_driving}
                        className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                          driverRecord.has_reckless_driving ? 'bg-amber-500' : 'bg-slate-300'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDriverRecord({ ...driverRecord, has_reckless_driving: !driverRecord.has_reckless_driving });
                        }}
                      >
                        <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                          driverRecord.has_reckless_driving ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Section 2: Driving Record */}
                <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="px-5 py-4 bg-slate-100/50 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-sm font-bold">2</span>
                      Driving Record
                    </h3>
                  </div>
                  <div className="p-5 space-y-6">
                    {/* Speeding Tickets */}
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-3 block">
                        Speeding tickets in the last 3 years
                      </Label>
                      <RadioGroup
                        value={driverRecord.speeding_tickets.toString()}
                        onValueChange={(value) => 
                          setDriverRecord({ ...driverRecord, speeding_tickets: parseInt(value) })
                        }
                        className="grid grid-cols-4 gap-3"
                      >
                        {["0", "1", "2", "3+"].map((opt, idx) => (
                          <div key={opt}>
                            <RadioGroupItem 
                              value={idx.toString()} 
                              id={`speeding-${idx}`}
                              data-testid={`speeding-${idx}`}
                              className="peer sr-only"
                            />
                            <Label 
                              htmlFor={`speeding-${idx}`} 
                              className="flex flex-col items-center justify-center h-20 p-4 bg-white border-2 border-slate-200 rounded-xl cursor-pointer transition-all hover:border-blue-300 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50"
                            >
                              <span className="text-lg font-bold text-slate-800">{opt}</span>
                              <span className="text-xs text-slate-500 mt-1">{opt === "0" ? "None" : opt === "1" ? "ticket" : "tickets"}</span>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    {/* At-Fault Accidents */}
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-3 block">
                        At-fault accidents (you were cited)
                      </Label>
                      <RadioGroup
                        value={driverRecord.cited_accidents.toString()}
                        onValueChange={(value) => 
                          setDriverRecord({ ...driverRecord, cited_accidents: parseInt(value) })
                        }
                        className="grid grid-cols-4 gap-3"
                      >
                        {["0", "1", "2", "3+"].map((opt, idx) => (
                          <div key={opt}>
                            <RadioGroupItem 
                              value={idx.toString()} 
                              id={`cited-${idx}`}
                              data-testid={`cited-${idx}`}
                              className="peer sr-only"
                            />
                            <Label 
                              htmlFor={`cited-${idx}`} 
                              className="flex flex-col items-center justify-center h-20 p-4 bg-white border-2 border-slate-200 rounded-xl cursor-pointer transition-all hover:border-blue-300 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50"
                            >
                              <span className="text-lg font-bold text-slate-800">{opt}</span>
                              <span className="text-xs text-slate-500 mt-1">{opt === "0" ? "None" : ""}</span>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    {/* Not-at-Fault Accidents */}
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-3 block">
                        Not-at-fault accidents (you were not cited)
                      </Label>
                      <RadioGroup
                        value={driverRecord.uncited_accidents.toString()}
                        onValueChange={(value) => 
                          setDriverRecord({ ...driverRecord, uncited_accidents: parseInt(value) })
                        }
                        className="grid grid-cols-4 gap-3"
                      >
                        {["0", "1", "2", "3+"].map((opt, idx) => (
                          <div key={opt}>
                            <RadioGroupItem 
                              value={idx.toString()} 
                              id={`uncited-${idx}`}
                              data-testid={`uncited-${idx}`}
                              className="peer sr-only"
                            />
                            <Label 
                              htmlFor={`uncited-${idx}`} 
                              className="flex flex-col items-center justify-center h-20 p-4 bg-white border-2 border-slate-200 rounded-xl cursor-pointer transition-all hover:border-blue-300 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50"
                            >
                              <span className="text-lg font-bold text-slate-800">{opt}</span>
                              <span className="text-xs text-slate-500 mt-1">{opt === "0" ? "None" : ""}</span>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>
                </div>

                {/* Section 3: Special Discount */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl border-2 border-blue-200 overflow-hidden">
                  <div className="p-5">
                    <div 
                      className="flex items-center justify-between gap-6 cursor-pointer"
                      onClick={() => setDriverRecord({ ...driverRecord, is_first_responder_military: !driverRecord.is_first_responder_military })}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="p-3 bg-blue-600 rounded-xl flex-shrink-0">
                          <Gift className="h-6 w-6 text-white" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                              First Responder / Military Discount
                            </span>
                            <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-semibold rounded-full">
                              SAVE 10%
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            Available for first responders, active military, and veterans
                          </p>
                          <p className="text-xs text-slate-500 italic mt-1">
                            Proof of eligibility will be verified before the discount is applied.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="military-discount-checkbox"
                        role="switch"
                        aria-checked={driverRecord.is_first_responder_military}
                        className={`relative w-14 h-8 rounded-full transition-colors flex-shrink-0 ${
                          driverRecord.is_first_responder_military ? 'bg-green-500' : 'bg-slate-300'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDriverRecord({ ...driverRecord, is_first_responder_military: !driverRecord.is_first_responder_military });
                        }}
                      >
                        <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${
                          driverRecord.is_first_responder_military ? 'translate-x-6' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>
                    {driverRecord.is_first_responder_military && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <div className="flex items-center gap-2 text-green-700 bg-green-100 px-3 py-2 rounded-lg">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">10% discount will be applied to your quote</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Major Violations Disqualification Notice */}
                {hasMajorViolations && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 sm:p-6 mt-6" data-testid="major-violations-block">
                    {/* Header - Stacked on mobile, side-by-side on desktop */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:gap-4">
                      {/* Icon and Title Row - Always together */}
                      <div className="flex items-center gap-3 mb-3 sm:mb-0">
                        <div className="p-2 sm:p-3 bg-red-100 rounded-full flex-shrink-0">
                          <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-red-800 sm:hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Unable to Offer Coverage
                        </h3>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <h3 className="hidden sm:block text-xl font-bold text-red-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Unable to Offer Coverage
                        </h3>
                        <p className="text-red-700 mb-4 text-sm sm:text-base">
                          Based on your driving history, we're unable to provide Diminished Value Protection at this time. 
                          Our underwriting guidelines require a clean record for the following:
                        </p>
                        <ul className="space-y-2 mb-4">
                          {driverRecord.has_dui && (
                            <li className="flex items-center gap-2 text-red-700 text-sm sm:text-base">
                              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                              <span>DUI/DWI conviction within the past 3 years</span>
                            </li>
                          )}
                          {driverRecord.has_license_suspension && (
                            <li className="flex items-center gap-2 text-red-700 text-sm sm:text-base">
                              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                              <span>License suspension within the past 3 years</span>
                            </li>
                          )}
                          {driverRecord.has_reckless_driving && (
                            <li className="flex items-center gap-2 text-red-700 text-sm sm:text-base">
                              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                              <span>Reckless driving citation within the past 3 years</span>
                            </li>
                          )}
                        </ul>
                        <div className="bg-white border border-red-200 rounded-xl p-3 sm:p-4">
                          <p className="text-sm text-slate-700 mb-2 sm:mb-3">
                            <strong>What you can do:</strong>
                          </p>
                          <ul className="text-sm text-slate-600 space-y-2">
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 mt-0.5">•</span>
                              <span>If you believe this information is incorrect, please update your selections above.</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 mt-0.5">•</span>
                              <span>You may reapply after the violation period has passed.</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 mt-0.5">•</span>
                              <span>Contact us at <a href="tel:1-833-567-7729" className="text-blue-600 hover:underline font-medium">1-833-LOSSPAY</a> if you have questions.</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Coverage Selection */}
          {currentStep === 3 && (
            <div className="p-6 sm:p-8 animate-fade-in">
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Choose Your Equity Protection Level
              </h2>
              <p className="text-slate-700 mb-6">
                Your protection limits are based on a vehicle value of {formatCurrency(parseFloat(vehicleValue) || 35000)}
              </p>

              {/* Coverage Level Cards */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {COVERAGE_LEVELS.map((level) => {
                  const coverageAmount = Math.min((parseFloat(vehicleValue) || 35000) * (level.percentage / 100), 100000);
                  return (
                    <button
                      key={level.level}
                      data-testid={`coverage-${level.level}`}
                      onClick={() => setCoverageLevel(level.level)}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                        coverageLevel === level.level
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      {level.badge && (
                        <span className={`absolute -top-3 left-4 text-white text-xs px-2 py-1 rounded-full ${
                          level.badge === 'Best Protection' ? 'bg-blue-600' :
                          level.badge === 'Most Popular' ? 'bg-green-600' :
                          'bg-slate-600'
                        }`}>
                          {level.badge}
                        </span>
                      )}
                      <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {level.name}
                      </h3>
                      <p className="text-2xl font-bold text-blue-600 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Up to {formatCurrency(coverageAmount)}
                      </p>
                      <p className="text-sm text-slate-700 mt-2">{level.description}</p>
                    </button>
                  );
                })}
              </div>

              {/* Deductible Selection */}
              <div className="mb-8">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">
                  Select Deductible
                </Label>
                <div className="grid grid-cols-4 gap-3">
                  {DEDUCTIBLE_OPTIONS.map((option) => (
                    <button
                      key={option.level}
                      data-testid={`deductible-${option.level}`}
                      onClick={() => setDeductibleLevel(option.level)}
                      className={`p-3 rounded-lg border-2 text-center transition-all min-h-[72px] flex flex-col items-center justify-center ${
                        deductibleLevel === option.level
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <p className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {option.amount === 0 ? '$0' : formatCurrency(option.amount)}
                      </p>
                      <p className="text-xs text-slate-700">{option.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Frequency */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">
                  Payment Frequency
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {PAYMENT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      data-testid={`payment-${option.value}`}
                      onClick={() => setPaymentFrequency(option.value)}
                      className={`flex flex-col p-3 rounded-lg border-2 text-left transition-all ${
                        paymentFrequency === option.value
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <span className="font-medium text-slate-900">{option.label}</span>
                      <span className="text-xs text-slate-700">{option.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Pay */}
          {currentStep === 4 && (
            <div className="p-6 sm:p-8 animate-fade-in">
              {/* Resumed Quote Banner */}
              {isResuming && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                  <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-900">Welcome Back!</h4>
                    <p className="text-sm text-blue-700">
                      Your saved quote has been restored. You can edit your coverage options using the "Edit" button below, or proceed directly to checkout.
                    </p>
                  </div>
                </div>
              )}
              
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Review Your Quote
              </h2>
              <p className="text-slate-700 mb-6">Review your coverage details and proceed to checkout</p>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
                  <p className="text-slate-700">Calculating your quote...</p>
                </div>
              ) : quote ? (
                <>
                  {!quote.is_eligible ? (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                      <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                      <h3 className="text-lg font-bold text-red-700 mb-2">Unable to Offer Coverage</h3>
                      <p className="text-red-600">{quote.rejection_reason}</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => {
                          setCurrentStep(2);
                          setTimeout(() => {
                            window.scrollTo({ top: 0, behavior: window.innerWidth < 640 ? 'auto' : 'smooth' });
                          }, 50);
                        }}
                      >
                        Review Driver History
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Vehicle Summary */}
                      <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200">
                        <div className="flex items-center gap-3 mb-3">
                          <Car className="h-5 w-5 text-slate-600" />
                          <span className="font-medium text-slate-700">Vehicle</span>
                        </div>
                        {(() => {
                          const vi = quote.vehicle_info || vehicleInfo || {};
                          return (
                            <>
                              <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {vi.year} {vi.make} {vi.model}{vi.trim ? ` ${vi.trim}` : ''}
                              </p>
                              <p className="text-sm text-slate-700 font-mono">{quote.vin}</p>
                              <p className="text-sm text-slate-700 mt-1">
                                Value: {formatCurrency(parseInt(vehicleValue, 10) || vi.estimated_value || 0)}
                              </p>
                            </>
                          );
                        })()}
                      </div>

                      {/* Coverage Details */}
                      <div className="bg-white rounded-xl p-4 mb-6 border border-slate-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Shield className="h-5 w-5 text-blue-600" />
                            <span className="font-medium text-slate-700">Coverage</span>
                          </div>
                          {isResuming && (
                            <button
                              onClick={() => {
                                setCurrentStep(3);
                                setIsResuming(false);
                                setTimeout(() => {
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }, 50);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                              data-testid="edit-coverage-btn"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                              Edit
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-slate-700">Coverage Level</p>
                            <p className="font-bold text-slate-900">{quote.coverage_level_name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-700">Coverage Amount</p>
                            <p className="font-bold text-slate-900">{formatCurrency(quote.coverage_amount)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-700">Deductible</p>
                            <p className="font-bold text-slate-900">{quote.deductible_name} ({formatCurrency(quote.deductible_amount)})</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-700">Payment</p>
                            <p className="font-bold text-slate-900 capitalize">{quote.payment_frequency.replace('_', '-')}</p>
                          </div>
                        </div>
                        {quote.first_responder_discount > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-sm text-green-600 font-medium">
                              First Responder/Military Discount Applied: -{formatCurrency(quote.first_responder_discount)}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Pricing Breakdown */}
                      <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200">
                        <div className="space-y-2">
                          <div className="flex justify-between text-slate-700">
                            <span>Base Premium</span>
                            <span>{formatCurrency(quote.base_premium)}</span>
                          </div>
                          <div className="flex justify-between text-slate-700">
                            <span>State Taxes</span>
                            <span>{formatCurrency(quote.taxes_and_fees)}</span>
                          </div>
                          <div className="flex justify-between text-slate-700">
                            <span>Policy Fee</span>
                            <span>{formatCurrency(quote.admin_fee)}</span>
                          </div>
                          <div className="border-t border-blue-200 pt-2 mt-2">
                            <div className="flex justify-between font-bold text-slate-900">
                              <span>Annual Total</span>
                              <span>{formatCurrency(quote.total_with_fees)}</span>
                            </div>
                          </div>
                          {quote.payment_frequency !== 'annual' && (
                            <div className="flex justify-between text-slate-700 text-sm">
                              <span>Payment per period</span>
                              <span>{formatCurrency(quote.payment_amount)}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-blue-200 text-center">
                          <p className="text-sm text-slate-700">Your {quote.payment_frequency.replace('_', '-')} payment</p>
                          <p className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {formatCurrency(quote.payment_amount)}
                          </p>
                        </div>
                      </div>

                      {/* Save Card Consent - Narrow, High-Clarity Authorization */}
                      <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="save-card-consent"
                            data-testid="save-card-consent-checkbox"
                            checked={saveCardConsent}
                            onCheckedChange={setSaveCardConsent}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <Label 
                              htmlFor="save-card-consent" 
                              className="text-sm font-medium text-slate-900 cursor-pointer"
                            >
                              Save this card for renewal and installment payments only
                            </Label>
                            <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                              I authorize LossPay to securely store this card with its payment processor and use it only for scheduled policy renewal or installment payments associated with this policy. This card will not be used for any other charges without my separate consent. I can update or remove my saved card at any time in my account settings.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-3">
                        <Button
                          data-testid="proceed-to-payment-btn"
                          size="lg"
                          variant="blue"
                          className="w-full py-6 text-lg font-semibold"
                          onClick={handleProceedToPayment}
                          disabled={loading}
                        >
                          {loading ? (
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          ) : (
                            <CreditCard className="h-5 w-5 mr-2" />
                          )}
                          Proceed to Payment
                        </Button>
                        <Button
                          data-testid="email-quote-btn"
                          variant="outline"
                          size="lg"
                          className="w-full py-6"
                          onClick={() => setEmailDialogOpen(true)}
                        >
                          <Mail className="h-5 w-5 mr-2" />
                          Email Quote
                        </Button>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-700">Unable to load quote. Please try again.</p>
                  <Button onClick={() => handleCalculateQuote()} className="mt-4">
                    Retry
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="px-6 sm:px-8 py-4 bg-slate-50 border-t border-slate-200 flex justify-between">
            <Button
              variant="ghost"
              onClick={currentStep === 1 ? () => navigate('/') : prevStep}
              className="text-slate-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {currentStep === 1 ? 'Back to Home' : 'Previous'}
            </Button>
            
            {currentStep < STEPS.length && (
              <Button
                data-testid="next-step-btn"
                onClick={nextStep}
                disabled={
                  loading || 
                  (currentStep === 1 && (!vehicleInfo || eligibilityError || valueEligibilityError || !valueConfirmed || (parseInt(vehicleValue, 10) || 0) < MINIMUM_VEHICLE_VALUE)) ||
                  (currentStep === 2 && hasMajorViolations)
                }
                variant="blue"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Email Quote Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Email Your Quote
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              We'll send your quote details to your email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="email-name" className="text-slate-800 font-medium">
                Your Name (optional)
              </Label>
              <Input
                id="email-name"
                data-testid="email-name-input"
                placeholder="John Doe"
                value={emailName}
                onChange={(e) => setEmailName(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email-address" className="text-slate-800 font-medium">
                Email Address
              </Label>
              <Input
                id="email-address"
                data-testid="email-address-input"
                type="email"
                placeholder="john@example.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <Button
              data-testid="send-email-btn"
              variant="blue"
              className="w-full mt-2"
              onClick={handleEmailQuote}
              disabled={sendingEmail || !emailAddress}
            >
              {sendingEmail ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Quote
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scan Your VIN - Dual Option Modal */}
      <Dialog open={scanModalOpen} onOpenChange={setScanModalOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start gap-3 mb-2">
              <div className="p-2 bg-[#3B5BDB]/10 rounded-xl">
                <Camera className="h-6 w-6 text-[#3B5BDB]" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Scan Your VIN
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-sm mt-0.5">
                  Take a photo or upload an image of your VIN
                </DialogDescription>
              </div>
            </div>

            {/* Helper text */}
            <p className="text-sm text-slate-600 mt-4 mb-5 text-center">
              Your VIN can be found on your dashboard, door jamb, registration, or insurance card.
            </p>

            {/* Dual option buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="scan-use-camera-btn"
                onClick={() => {
                  setScanModalOpen(false);
                  startCamera();
                }}
                className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-slate-200 hover:border-[#3B5BDB] hover:bg-[#3B5BDB]/5 transition-all group"
              >
                <Camera className="h-8 w-8 text-slate-500 group-hover:text-[#3B5BDB] transition-colors" />
                <span className="font-semibold text-slate-700 group-hover:text-[#3B5BDB] transition-colors">Use Camera</span>
              </button>
              <button
                data-testid="scan-upload-photo-btn"
                onClick={() => {
                  setScanModalOpen(false);
                  fileInputRef.current?.click();
                }}
                className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-slate-200 hover:border-[#3B5BDB] hover:bg-[#3B5BDB]/5 transition-all group"
              >
                <Upload className="h-8 w-8 text-slate-500 group-hover:text-[#3B5BDB] transition-colors" />
                <span className="font-semibold text-slate-700 group-hover:text-[#3B5BDB] transition-colors">Upload Photo</span>
              </button>
            </div>

            {/* AI Vision badge */}
            <div className="flex items-start gap-2.5 mt-5 pt-4 border-t border-slate-100">
              <div className="p-1.5 bg-purple-100 rounded-lg flex-shrink-0 mt-0.5">
                <svg className="h-4 w-4 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="6" height="6" rx="1" />
                  <rect x="16" y="2" width="6" height="6" rx="1" />
                  <rect x="2" y="16" width="6" height="6" rx="1" />
                  <rect x="16" y="16" width="6" height="6" rx="1" />
                  <path d="M8 5h8" /><path d="M5 8v8" /><path d="M19 8v8" /><path d="M8 19h8" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">AI Vision</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Instant detection for VIN stickers, with AI OCR fallback for printed VINs
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Camera Scan Dialog */}
      <Dialog open={cameraOpen} onOpenChange={(open) => { if (!open) stopCamera(); }}>
        <DialogContent className="sm:max-w-xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2 text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Camera className="h-5 w-5 text-blue-600" />
              Scan VIN
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Position the VIN label in the camera view and tap capture
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            {/* Camera Preview */}
            <div className="relative bg-black aspect-video">
              <video
                ref={(el) => {
                  videoRef.current = el;
                  if (el && streamRef.current) {
                    el.srcObject = streamRef.current;
                    el.play().catch(() => {});
                  }
                }}
                autoPlay
                playsInline
                muted
                webkit-playsinline="true"
                className="w-full h-full object-cover"
                style={{ WebkitTransform: 'translateZ(0)' }}
              />
              {/* Scanning overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="border-2 border-blue-500 border-dashed rounded-lg w-4/5 h-16 flex items-center justify-center">
                  <span className="text-blue-400 text-sm bg-black/50 px-2 py-1 rounded">
                    Align VIN here
                  </span>
                </div>
              </div>
              {/* Progress overlay when scanning */}
              {scanning && (
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
                  <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-3" />
                  <p className="text-white text-sm">Scanning VIN...</p>
                  <p className="text-blue-400 text-xs mt-1">{scanProgress}%</p>
                </div>
              )}
            </div>
            {/* Hidden canvas for image capture */}
            <canvas ref={canvasRef} className="hidden" />
            {/* Controls */}
            <div className="p-4 flex gap-3 justify-center bg-slate-100">
              <Button
                variant="outline"
                onClick={stopCamera}
                disabled={scanning}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                variant="blue"
                onClick={captureAndScan}
                disabled={scanning}
                data-testid="capture-vin-btn"
              >
                {scanning ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Camera className="h-4 w-4 mr-2" />
                )}
                {scanning ? 'Scanning...' : 'Capture & Scan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog open={imageUploadOpen} onOpenChange={(open) => { if (!open) cancelImageUpload(); }}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden">
          <div className="p-4 border-b bg-white flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ImageIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">VIN Photo Upload</DialogTitle>
              <p className="text-sm text-slate-500">
                We'll scan your photo for the VIN number
              </p>
            </div>
          </div>
          <div className="p-4">
            {uploadedImage && (
              <div className="relative rounded-lg overflow-hidden border-2 border-slate-200 bg-slate-100">
                <img 
                  src={uploadedImage} 
                  alt="Uploaded VIN" 
                  className="w-full h-auto max-h-80 object-contain"
                />
                {/* Processing overlay */}
                {processingImage && (
                  <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
                    <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-3" />
                    <p className="text-white text-sm">Processing image...</p>
                    <p className="text-blue-400 text-xs mt-1">{scanProgress}%</p>
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-3 text-center">
              Make sure the VIN is clearly visible in the image
            </p>
          </div>
          {/* Controls */}
          <div className="p-4 bg-slate-100 border-t space-y-2">
            <Button
              variant="blue"
              onClick={processUploadedImage}
              disabled={processingImage}
              data-testid="process-image-btn"
              className="w-full"
            >
              {processingImage ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              {processingImage ? 'Scanning...' : 'Scan for VIN'}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={cancelImageUpload}
                disabled={processingImage}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={processingImage}
                className="flex-1"
              >
                <Upload className="h-4 w-4 mr-2" />
                Choose Different
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
